// Shared UI pieces: buttons, fields, dialogs, toasts, status chips, brand wordmark, language switch.
import { api } from "../lib/api";
import { brand } from "../lib/brand";
import { applyLocale, errorText, i18n, t, type Locale } from "../lib/i18n";
import { Link } from "../lib/router";
import { session } from "../lib/session";
import { h, redraw, store, type Child, type Resource } from "../lib/ui";
import { Icon } from "./icons";

// ───────────── Toasts ─────────────

const toasts = store({ list: [] as { id: number; text: string; kind: "ok" | "error" }[] });
let toastId = 0;

export function toast(text: string, kind: "ok" | "error" = "ok") {
  const id = ++toastId;
  toasts.set({ list: [...toasts.list, { id, text, kind }] });
  setTimeout(() => toasts.set({ list: toasts.list.filter((x) => x.id !== id) }), kind === "error" ? 6000 : 3500);
}

export function toastError(e: unknown) {
  toast(errorText(e), "error");
}

export function Toasts() {
  return (
    <div class="toasts" role="status" aria-live="polite">
      {toasts.list.map((x) => (
        <div key={x.id} class={`toast ${x.kind === "error" ? "error" : ""}`}>
          <Icon name={x.kind === "error" ? "alert" : "check"} size={18} />
          <span>{x.text}</span>
        </div>
      ))}
    </div>
  );
}

// ───────────── Dialogs ─────────────

const dialogs = store({ stack: [] as { id: number; render: () => Child; onClose: () => void }[] });
let dialogId = 0;

/** Opens a dialog. `render` is called on every redraw; returns a close function. */
export function openDialog(render: (close: () => void) => Child, onClose?: () => void) {
  const id = ++dialogId;
  const close = () => {
    dialogs.set({ stack: dialogs.stack.filter((d) => d.id !== id) });
    onClose?.();
  };
  dialogs.set({ stack: [...dialogs.stack, { id, render: () => render(close), onClose: close }] });
  return close;
}

addEventListener("keydown", (e) => {
  if (e.key === "Escape" && dialogs.stack.length) dialogs.stack[dialogs.stack.length - 1].onClose();
});

export function Dialogs() {
  return (
    <div>
      {dialogs.stack.map((d) => (
        <div key={d.id} class="overlay" onMousedown={(e: MouseEvent) => e.target === e.currentTarget && d.onClose()}>
          {d.render()}
        </div>
      ))}
    </div>
  );
}

export function Dialog(props: { title: string; onClose: () => void; children?: any; footer?: Child; wide?: boolean }) {
  return (
    <div class={`dialog ${props.wide ? "wide" : ""}`} role="dialog" aria-modal="true" aria-label={props.title}>
      <div class="dialog-head">
        <h2>{props.title}</h2>
        <button class="icon-btn" onClick={props.onClose} aria-label={t("common.close")}>
          <Icon name="x" />
        </button>
      </div>
      <div class="dialog-body">{props.children}</div>
      {props.footer ? <div class="dialog-foot">{props.footer}</div> : null}
    </div>
  );
}

/** Confirmation dialog; resolves true when the person confirms. */
export function confirmDialog(o: { title: string; body: string; confirm: string; danger?: boolean }): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    openDialog(
      (c) => (
        <Dialog
          title={o.title}
          onClose={c}
          footer={[
            <button class="btn secondary" onClick={c}>{t("common.cancel")}</button>,
            <button class={`btn ${o.danger ? "danger" : ""}`} onClick={() => { done = true; resolve(true); c(); }}>{o.confirm}</button>,
          ]}
        >
          <p>{o.body}</p>
        </Dialog>
      ),
      () => !done && resolve(false),
    );
  });
}

// ───────────── Buttons & async actions ─────────────

const busy = new Set<string>();

/** Runs an async action once at a time per key, shows errors as a toast, redraws around it. */
export async function act<T>(key: string, fn: () => Promise<T>, ok?: string): Promise<T | undefined> {
  if (busy.has(key)) return;
  busy.add(key);
  redraw();
  try {
    const r = await fn();
    if (ok) toast(ok);
    return r;
  } catch (e) {
    toastError(e);
    return undefined;
  } finally {
    busy.delete(key);
    redraw();
  }
}

export const isBusy = (key: string) => busy.has(key);

export function Button(props: {
  kind?: "primary" | "secondary" | "ghost" | "danger" | "danger-outline" | "accent";
  size?: "small" | "large";
  block?: boolean;
  busy?: boolean;
  disabled?: boolean;
  type?: "button" | "submit";
  onClick?: (e: MouseEvent) => void;
  icon?: Parameters<typeof Icon>[0]["name"];
  children?: any;
  title?: string;
  form?: string;
}) {
  const cls = ["btn", props.kind && props.kind !== "primary" ? props.kind : "", props.size ?? "", props.block ? "block" : ""].join(" ");
  return (
    <button class={cls} type={props.type ?? "button"} disabled={props.disabled || props.busy} onClick={props.onClick} title={props.title} form={props.form}>
      {props.busy ? <span class="spin" aria-hidden="true" /> : props.icon ? <Icon name={props.icon} size={18} /> : null}
      {props.children}
    </button>
  );
}

// ───────────── Fields ─────────────

export function Field(props: { label: string; hint?: string; error?: string | null; children?: any; id?: string }) {
  return (
    <div class="field">
      <label htmlFor={props.id}>{props.label}</label>
      {props.children}
      {props.error ? <span class="err">{props.error}</span> : props.hint ? <span class="hint">{props.hint}</span> : null}
    </div>
  );
}

export function Select(props: { id?: string; name?: string; value?: string; options: { value: string; label: string }[]; onChange?: (v: string) => void; required?: boolean; disabled?: boolean }) {
  return (
    <select
      class="select"
      id={props.id}
      name={props.name}
      value={props.value ?? ""}
      required={props.required}
      disabled={props.disabled}
      onChange={(e: Event) => props.onChange?.((e.target as HTMLSelectElement).value)}
    >
      {props.options.map((o) => (
        <option value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export function Toggle(props: { checked: boolean; onChange: (v: boolean) => void; label?: string; disabled?: boolean }) {
  return (
    <label class="switch">
      <input type="checkbox" checked={props.checked} disabled={props.disabled} onChange={(e: Event) => props.onChange((e.target as HTMLInputElement).checked)} />
      <span class="track" aria-hidden="true" />
      {props.label ? <span>{props.label}</span> : null}
    </label>
  );
}

/** Reads a <form> into a plain object (strings; empty strings become null). */
export function formData(e: Event): Record<string, any> {
  e.preventDefault();
  const fd = new FormData(e.currentTarget as HTMLFormElement);
  const out: Record<string, any> = {};
  for (const [k, v] of fd.entries()) {
    const val = typeof v === "string" ? v.trim() : v;
    if (k in out) out[k] = ([] as any[]).concat(out[k], val);
    else out[k] = val === "" ? null : val;
  }
  return out;
}

/** Dollars typed by a person → integer cents. */
export function toCents(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/[\s$]/g, "").replace(",", "."));
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

export function centsToInput(c: number | null | undefined) {
  if (c === null || c === undefined) return "";
  return (c / 100).toFixed(c % 100 === 0 ? 0 : 2);
}

// ───────────── Status display ─────────────

export function Chip(props: { kind?: "good" | "attention" | "critical" | "info" | "accent" | "neutral"; plain?: boolean; children?: any }) {
  return <span class={`chip ${props.kind ?? ""} ${props.plain ? "plain" : ""}`}>{props.children}</span>;
}

export function HealthChip(props: { health: string }) {
  return <Chip kind={props.health as any}>{t(`health.${props.health}`)}</Chip>;
}

const TICKET_KIND: Record<string, any> = {
  awaiting_resident_approval: "accent", new: "info", triaged: "info", estimating: "info", awaiting_approval: "accent",
  dispatched: "info", in_progress: "info", awaiting_signoff: "accent", invoiced: "neutral", paid: "good",
  closed: "good", cancelled: "neutral", disputed: "critical",
};
export function TicketStatus(props: { status: string }) {
  return <Chip kind={TICKET_KIND[props.status]}>{t(`ticketStatus.${props.status}`)}</Chip>;
}

const VISIT_KIND: Record<string, any> = {
  scheduled: "neutral", notified: "info", time_confirmed: "info", entry_approved: "good", in_progress: "accent", completed: "good", cancelled: "neutral",
};
export function VisitStatus(props: { status: string }) {
  return <Chip kind={VISIT_KIND[props.status]}>{t(`visitStatus.${props.status}`)}</Chip>;
}

export function Prio(props: { p: string }) {
  return <span class={`prio ${props.p}`} title={t(`priority.${props.p}`)}>{props.p}</span>;
}

// ───────────── Data states ─────────────

export function Loading(props: { rows?: number }) {
  return (
    <div class="stack tight" style="padding:18px 22px" aria-busy="true" aria-label={t("common.loading")}>
      {Array.from({ length: props.rows ?? 3 }, (_, i) => <div class="skeleton" style={{ width: `${90 - i * 17}%` }} />)}
    </div>
  );
}

export function LoadError(props: { error: any; retry?: () => void }) {
  return (
    <div class="empty">
      <strong>{t("common.loadFailed")}</strong>
      <span>{errorText(props.error)}</span>
      {props.retry ? <Button kind="secondary" onClick={props.retry}>{t("common.retry")}</Button> : null}
    </div>
  );
}

/** Renders a resource: skeleton while loading, error with retry, otherwise `view(data)`. */
export function Async<T>(r: Resource<T>, view: (data: T) => Child, rows = 3): Child {
  if (r.error && !r.data) return <LoadError error={r.error} retry={() => r.reload()} />;
  if (r.data === undefined) return <Loading rows={rows} />;
  return view(r.data);
}

export function Empty(props: { title: string; body?: string; action?: Child }) {
  return (
    <div class="empty">
      <strong>{props.title}</strong>
      {props.body ? <span>{props.body}</span> : null}
      {props.action}
    </div>
  );
}

// ───────────── Brand & language ─────────────

export function Wordmark(props: { href?: string; showName?: boolean }) {
  const b = brand.current;
  const inner = b.wordmarkUrl ? (
    <img class="full" src={b.wordmarkUrl} alt={b.appName} />
  ) : (
    [<img src={b.markUrl} alt="" />, props.showName === false ? null : <span>{b.appName}</span>]
  );
  return props.href ? <Link class="wordmark" href={props.href}>{inner}</Link> : <span class="wordmark">{inner}</span>;
}

export function LangSwitch() {
  const choose = async (l: Locale) => {
    applyLocale(l);
    if (session.me) {
      session.me.locale = l;
      await api.patch("/api/me", { locale: l }).catch(() => {});
    }
  };
  if (i18n.enabled.length < 2) return null;
  return (
    <div class="lang-switch" role="group" aria-label={t("common.language")}>
      {i18n.enabled.map((l) => (
        <button aria-pressed={String(i18n.locale === l)} onClick={() => choose(l)} lang={l} title={t(`languages.${l}`)}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/** Copy text with a toast. */
export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast(t("common.copied"));
  } catch {
    toast(t("errors.copy_failed"), "error");
  }
}

/** Shows a one-time link (invite / reset) so staff can send it to the person. */
export function showLinkDialog(title: string, url: string, body: string) {
  openDialog((close) => (
    <Dialog title={title} onClose={close} footer={[<Button kind="secondary" onClick={close}>{t("common.done")}</Button>, <Button icon="copy" onClick={() => copyText(url)}>{t("common.copyLink")}</Button>]}>
      <div class="stack">
        <p>{body}</p>
        <input class="input" readOnly value={url} onFocus={(e: FocusEvent) => (e.target as HTMLInputElement).select()} />
      </div>
    </Dialog>
  ));
}
